<template>
  <div>
    <div
      class="ghl-form-survey-text-style-editor flex flex-wrap items-center gap-1.5 px-2 py-1.5"
      :style="{ borderBottom: '1px solid #E5E7EB' }"
    >
      <RTEControlHistory type="undo" :editor="editor" />
      <RTEControlHistory type="redo" :editor="editor" />

      <UIDivider :vertical="true" />

      <div class="overflow-menu-icon">
        <RTEControlColorPicker :editor="editor" tooltip-label="Text color" />
      </div>
      <RTEControlLineHeight :editor="editor" :fallback-line-height="'1'" />

      <RTEControlEmbedLink :editor="editor" :modal-container="modalContainer" />
      <RTEControlEmbedImage
        :editor="editor"
        external-embed-text="Upload"
        :external-embedded-image="externalEmbeddedImage"
        :modal-container="modalContainer"
        @trigger-external-embed="triggerExternalEmbed"
      />
      <input
        ref="fileInputRef"
        class="hidden"
        type="file"
        accept="image/png, image/jpeg, image/gif, image/webp"
        @change="onFileChange"
      />
      <UIDivider v-if="!mobileInline" :vertical="true" />

      <RTEControlCustomVariablePicker
        :editor="editor"
        :is-submit-message="isSubmitMessage === true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  RTEControlColorPicker,
  RTEControlEmbedImage,
  RTEControlEmbedLink,
  RTEControlHistory,
  RTEControlLineHeight,
} from "@gohighlevel/ghl-text-editor";
import { UIDivider } from "@gohighlevel/ghl-ui";
// @ts-ignore - editor types available at runtime
import { FormServices } from "@/service/FormServices";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import { Editor } from "@tiptap/vue-3";
import { ref } from "vue";
import RTEControlCustomVariablePicker from "./RTEControlCustomVariablePicker.vue";

type Props = {
  editor: Editor;
  mobileInline?: boolean;
  isSubmitMessage?: boolean;
  // When the editor is used inside a modal, provide a container
  // element or selector so embed modals teleport inside it.
  modalContainer?: string | HTMLElement | null;
};
defineProps<Props>();

const store = useAppStore();
const fileInputRef = ref<HTMLInputElement | null>(null);
const externalEmbeddedImage = ref<string | null>(null);

const triggerExternalEmbed = () => fileInputRef.value?.click();

const onFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  const form = new FormData();
  form.append("locationId", (store as any).locationId);
  form.append("file", file);
  form.append("type", "builder");

  try {
    let response: any;
    if ((store as any).formId) {
      form.append("formId", (store as any).formId);
      response = await FormServices.imageUpload(form);
    } else {
      form.append("surveyId", (store as any).surveyId);
      response = await SurveyServices.imageUpload(form);
    }
    const url = `https://images.leadconnectorhq.com/image/f_webp/q_85/r_1000/u_${response.data.imageUrl}`;
    externalEmbeddedImage.value = url;
  } catch (e) {
    // no-op; rely on upstream error handling
  } finally {
    if (input) {
      input.value = "";
    }
  }
};
</script>

<style scoped>
.ghl-form-survey-text-style-editor {
  user-select: none;
  -webkit-user-select: none;
}

.ghl-form-survey-text-style-editor :deep(.n-base-selection .n-base-selection-label),
.ghl-form-survey-text-style-editor
  :deep(.n-button--default-type:not(.icon-only):not(.hl-active-btn)) {
  background-color: transparent !important;
}
</style>
